import { Checkbox } from '@monksflow/monks-ui';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { BrandFontItem } from '../api/brandFont.api';
import { specimenFamily, type FontSpecimen } from '../model/font-specimen';
import { useFontFaces } from '../model/useFontFaces';

/** What the row shows, and so what a screen reader announces when it is picked. */
const displayName = (font: BrandFontItem) => font.familyName ?? font.filename;

/** A stable identity for the default, so an absent list cannot restart the face registration. */
const NO_SPECIMENS: readonly FontSpecimen[] = [];

/**
 * The row's fill, per state, on the same two tints `MediaTable` takes from Tessra's own datatable --
 * `active` for the open row, `accent` for a ticked one -- so a list holding fonts and photographs
 * says the same thing the same way in both halves.
 */
const ROW_CLASS = {
  open: 'bg-border',
  selected: 'bg-accent',
} as const;

/**
 * The library's fonts as rows, for a list view.
 *
 * Each row opens with an "Aa" the size of `MediaTable`'s thumbnail and set at the same offset, so a
 * list holding both kinds reads down one column of pictures rather than two ragged ones. It is the
 * font's own face where the file has arrived and the interface font where it has not, which is the
 * honest fallback: the font exists, its bytes did not.
 *
 * It behaves as `MediaTable` does, because the two lists sit in one column and a person picks across
 * both: a checkbox column says what is selected, clicking anywhere else in the row opens it, and the
 * open one is tinted rather than marked at its edge.
 */
export const FontGrid = ({
  fonts,
  specimens = NO_SPECIMENS,
  selectedIds,
  openFontId,
  onChoose,
  onToggleSelect,
}: {
  fonts: readonly BrandFontItem[];
  /**
   * The same fonts with their signed urls resolved. Left out, the samples fall back to the
   * interface font: registering a face needs a url, which only a host that can sign one has.
   *
   * Memoise this in the host. A fresh array each render re-registers every `FontFace` with the
   * document.
   */
  specimens?: readonly FontSpecimen[];
  /** Fonts ticked for a bulk action. Present but empty still means picking, as in `MediaGrid`. */
  selectedIds?: ReadonlySet<string>;
  /** The row the detail panel is describing, tinted with the table's `active` rather than its
      `selected`, so the two states stay legible on one row. */
  openFontId?: string;
  /** Omit it for a read-only surface, whose rows are then read rather than opened. */
  onChoose?: (fontId: string) => void;
  /** Omit it and the checkbox column is left out, for a surface with no bulk action. */
  onToggleSelect?: (fontId: string) => void;
}) => {
  const { t } = useTranslation('tools');
  useFontFaces(specimens);

  if (fonts.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('brandFont.empty')}</p>;
  }

  const selectable = onToggleSelect !== undefined;

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border-light text-left text-xs text-muted-foreground">
          {selectable && (
            <th className="w-11 px-2 py-2 font-medium">
              {/* Named for a screen reader reading the header row; a printed "Select" over a column
                  of ticks is a word doing nothing for anyone looking at it. */}
              <span className="sr-only">{t('brandFont.columns.select')}</span>
            </th>
          )}
          <th className="px-2 py-2 font-medium">{t('brandFont.columns.family')}</th>
          <th className="px-2 py-2 font-medium">{t('brandFont.columns.weight')}</th>
          <th className="px-2 py-2 font-medium">{t('brandFont.columns.style')}</th>
          <th className="px-2 py-2 font-medium">{t('brandFont.columns.added')}</th>
        </tr>
      </thead>
      <tbody>
        {fonts.map((font) => {
          const selected = selectedIds?.has(font.id) ?? false;
          const open = font.id === openFontId;

          return (
            <tr
              key={font.id}
              data-testid="brand-font-row"
              // The mouse affordance is the whole row, the way `MediaTable` and every other file
              // list behaves. The button in the name cell is what a keyboard and a screen reader
              // get, and it stops its own click here so one click is not counted twice.
              {...(onChoose !== undefined ? { onClick: () => onChoose(font.id) } : {})}
              // The open row takes precedence over a ticked one: its checkbox still says it is in
              // the selection, while nothing else in the row would say the panel is describing it.
              className={`group border-b border-border-light last:border-b-0 ${
                onChoose !== undefined ? 'cursor-pointer' : ''
              } ${open ? ROW_CLASS.open : selected ? ROW_CLASS.selected : ''}`}
            >
              {selectable && (
                <td className="py-2 pl-1 pr-2">
                  <Checkbox
                    aria-label={t('brandFont.selectFont', { name: displayName(font) })}
                    checked={selected}
                    // Ticking is the one thing in this row that does not open the font, so the click
                    // stops here rather than reaching the row behind it.
                    onClick={(event) => event.stopPropagation()}
                    onCheckedChange={() => onToggleSelect(font.id)}
                  />
                </td>
              )}
              <td className="px-2 py-2 text-foreground">
                <div className="flex min-w-0 items-center gap-2">
                  {/* Hidden from a screen reader: the family name is right beside it, and "Aa" read
                      aloud says nothing a picture of the letters does. */}
                  <span
                    aria-hidden="true"
                    data-testid="brand-font-row-specimen"
                    className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded border border-border bg-muted leading-none text-foreground"
                    style={{
                      fontFamily: `"${specimenFamily(font.id)}", var(--font-sans, sans-serif)`,
                      ...(font.weight !== null ? { fontWeight: font.weight } : {}),
                      ...(font.style !== null ? { fontStyle: font.style } : {}),
                    }}
                  >
                    {t('brandFont.specimenSample')}
                  </span>
                  {onChoose === undefined ? (
                    <span className="truncate" title={displayName(font)}>
                      {displayName(font)}
                    </span>
                  ) : (
                    <button
                      type="button"
                      aria-current={open ? 'true' : undefined}
                      title={displayName(font)}
                      className="min-w-0 truncate text-left"
                      onClick={(event) => {
                        event.stopPropagation();
                        onChoose(font.id);
                      }}
                    >
                      {displayName(font)}
                    </button>
                  )}
                </div>
              </td>
              <td className="px-2 py-2 text-muted-foreground">{font.weight ?? '-'}</td>
              <td className="px-2 py-2 text-muted-foreground">{font.style ?? '-'}</td>
              <td className="px-2 py-2 text-muted-foreground">
                {/* eslint-disable-next-line i18next/no-literal-string -- date-fns format pattern, machine input to format() rather than translator-facing copy */}
                {format(new Date(font.createdAt), 'd MMM yyyy')}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
